import { useQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  Fab,
  List,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { FragmentType, graphql } from '../../__generated__';
import { useState } from 'react';
import SatelliteAgentItem, {
  SatelliteAgentListItemFragment,
} from './SatelliteAgentListItem';
import EditSatelliteAgent from './EditSatelliteAgent';
import DeleteSatelliteAgent from './DeleteSatelliteAgent';
import ResetAgentBalance from './ResetAgentBalance';
import { pollServer } from '../../graphql/utils';
import Copyright from '../../components/common/Copyright';
import useGmdUserInfo from '../../hooks/gmdUserInfo';

export const getSatelliteAgentsQuery = graphql(/* GraphQL */ `
  query SatelliteAgents {
    satelliteAgents {
      ...SatelliteAgentListItemFragment
    }
  }
`);

const SatelliteAgentsContainer = () => {
  const { data, loading, error, startPolling, stopPolling } = useQuery(
    getSatelliteAgentsQuery
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openResetBalance, setOpenResetBalance] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] =
    useState<FragmentType<typeof SatelliteAgentListItemFragment>>();
  const { canAddSatelliteAgents } = useGmdUserInfo();

  const onCloseEdit = (itemsChanged?: boolean) => {
    setOpenEdit(false);
    pollServer(startPolling, stopPolling);
  };

  const onOpenAdd = () => {
    stopPolling();
    setSelectedAgent(undefined);
    setOpenAdd(true);
  };

  const onOpenEdit = () => {
    stopPolling();
    setOpenEdit(true);
  };

  const onOpenDelete = () => {
    stopPolling();
    setOpenDelete(true);
  };

  const onOpenResetBalance = () => {
    stopPolling();
    setOpenResetBalance(true);
  };

  const onCloseAdd = (itemsChanged?: boolean) => {
    setOpenAdd(false);
    pollServer(startPolling, stopPolling);
  };

  const onCloseDelete = (deleted?: boolean) => {
    setOpenDelete(false);
    if (deleted) {
      pollServer(startPolling, stopPolling);
    }
  };

  const onCloseResetBalance = (reset?: boolean) => {
    setOpenResetBalance(false);
    if (reset) {
      pollServer(startPolling, stopPolling);
    }
  };

  if (error) {
    return <div>{error?.message}</div>;
  }

  const agents = data?.satelliteAgents || [];

  return (
    <Container component="main">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {(!agents || agents.length === 0) && <div>No satellite agents</div>}
        {agents && agents.length > 0 && (
          <List
            sx={{ width: '100%', maxWidth: 700, bgcolor: 'background.paper' }}
          >
            {agents.map((agent, index) => {
              if (agent === null) return null;
              return (
                <SatelliteAgentItem
                  key={index}
                  agent={agent}
                  setSelectedAgent={setSelectedAgent}
                  onEdit={onOpenEdit}
                  onDelete={onOpenDelete}
                  onResetBalance={onOpenResetBalance}
                />
              );
            })}
          </List>
        )}
        {canAddSatelliteAgents && (
          <Fab
            sx={{ position: 'fixed', bottom: 50, right: 50 }}
            aria-label={'Add'}
            color={'primary'}
            disabled={loading}
            onClick={onOpenAdd}
          >
            <AddIcon />
          </Fab>
        )}
        {loading && <CircularProgress />}
      </Box>
      {selectedAgent && (
        <>
          <EditSatelliteAgent
            open={openEdit}
            agent={selectedAgent}
            onClose={onCloseEdit}
          />
          <DeleteSatelliteAgent
            open={openDelete}
            agent={selectedAgent}
            onClose={onCloseDelete}
          />
          <ResetAgentBalance
            open={openResetBalance}
            agent={selectedAgent}
            onClose={onCloseResetBalance}
          />
        </>
      )}
      {openAdd && (
        <EditSatelliteAgent
          open={openAdd}
          agent={selectedAgent}
          onClose={onCloseAdd}
        />
      )}
      <Copyright />
    </Container>
  );
};

export default SatelliteAgentsContainer;
